import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PastEditions from "@/components/site/PastEditions";
import ContactCTA from "@/components/site/ContactCTA";
import { Reveal, SafeImg } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets, leaderPortrait, leaderMedia } from "@/lib/assets";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import JsonLd from "@/components/site/JsonLd";
import { graph, breadcrumbNode, leaderNode } from "@/lib/schema";
import NotFound from "@/pages/NotFound";
import { LEGACY_LEADER_SLUGS } from "@/lib/legacySlugs";

export default function LeaderDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const assets = useAssets();
  const [leader, setLeader] = useState(null);
  const [tours, setTours] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/leaders/${slug}`).then((r) => setLeader(r.data)).catch(() => setNotFound(true));
    api.get("/tours").then((r) => setTours(r.data.filter((x) => (x.leader_slugs || []).includes(slug)))).catch(() => {});
  }, [slug]);

  const seoLeader = leader ? L(leader, lang) : null;
  const seoDescription = seoLeader
    ? ((seoLeader.bio || "").replace(/\s+/g, " ") ||
       t("seo.leader.fallback").replace("{name}", seoLeader.name).replace("{role}", seoLeader.role || "").replace("{territory}", seoLeader.territory || "")
      ).slice(0, 157)
    : "";
  useSeo({
    title: seoLeader ? `${seoLeader.name} — ${seoLeader.role} | De Los Cielos` : "Tour Leader | De Los Cielos",
    description: seoDescription,
    image: seoLeader ? leaderPortrait(assets, seoLeader) : undefined,
    path: `/leader/${slug}`,
    type: "profile",
    noindex: notFound,
  });

  if (LEGACY_LEADER_SLUGS[slug]) return <Navigate to={`/leader/${LEGACY_LEADER_SLUGS[slug]}`} replace />;

  if (notFound) return <NotFound />;

  const jsonLd = leader && seoLeader
    ? graph([
        leaderNode({ leader: { ...seoLeader, slug: leader.slug }, portrait: leaderPortrait(assets, seoLeader), lang }),
        breadcrumbNode([
          { name: "Home", path: "/" },
          { name: t("nav.leaders"), path: "/leader" },
          { name: seoLeader.name, path: `/leader/${leader.slug}` },
        ], lang),
      ])
    : null;

  if (!leader) {
    return <div className="bg-obsidian min-h-screen flex items-center justify-center"><div className="font-mono text-xs tracking-[0.3em] text-copper animate-slowpulse">CARICAMENTO…</div></div>;
  }

  const l = L(leader, lang);
  const portrait = leaderPortrait(assets, l);
  const media = leaderMedia(assets, l);
  const locTours = tours.map((x) => L(x, lang));

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-leader" data={jsonLd} />

      <section className="pt-32 sm:pt-40 pb-16">
        <div className="container-x">
          <Link to="/leader" className="font-mono text-[11px] uppercase tracking-[0.25em] text-copperlight flex items-center gap-2 mb-10 w-max" data-testid="leader-back-link">
            <ArrowLeft className="h-4 w-4" /> {t("nav.leaders")}
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            <Reveal className="md:col-span-5">
              <div className="relative border border-copper/25 overflow-hidden" data-testid="leader-portrait">
                <SafeImg src={portrait} alt={l.name} className="w-full aspect-[3/4] object-cover" />
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-copperlight">
                  <MapPin className="h-3.5 w-3.5" /> {l.territory}
                </div>
              </div>
            </Reveal>
            <div className="md:col-span-7">
              <Reveal>
                <p className="eyebrow mb-4" data-testid="leader-role">{l.role}</p>
                <h1 className="font-serif font-semibold text-5xl sm:text-6xl leading-[0.98]" data-testid="leader-name">{l.name}</h1>
                <p className="mt-8 text-lg text-cream2 leading-relaxed whitespace-pre-line" data-testid="leader-bio">{l.bio}</p>
              </Reveal>
              {l.philosophy && (
              <Reveal delay={0.15}>
                <blockquote className="mt-10 border-l-2 border-copper pl-6" data-testid="leader-philosophy">
                  <p className="font-serif italic text-2xl sm:text-3xl text-cream leading-snug">“{l.philosophy}”</p>
                  <cite className="block mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-copper not-italic">{t("manifesto.eyebrow")} — {l.name}</cite>
                </blockquote>
              </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      <PastEditions title={t("leader.media.title")} photos={media.photos} videos={media.videos} audio={media.audio} alt={l.name} testId="leader-past-editions" />

      {locTours.length > 0 && (
        <section className="pb-24 sm:pb-32" data-testid="leader-tours">
          <div className="container-x">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-cream mb-10">{t("home.exped.title")}</h2>
            </Reveal>
            <div className="space-y-4">
              {locTours.map((tour, i) => (
                <Reveal key={tour.id} delay={i * 0.06}>
                  <Link to={`/viaggi/${tour.slug}`} className="group flex items-center gap-6 card-dark p-5 hover:border-copper/40 transition-colors" data-testid={`leader-tour-${tour.slug}`}>
                    <SafeImg src={(assets && assets.tours && assets.tours[tour.slug] && assets.tours[tour.slug].cover) || (tour.images && tour.images[0])} alt={tour.title} className="h-16 w-24 shrink-0 object-cover" />
                    <div>
                      <p className="font-serif text-xl sm:text-2xl text-cream group-hover:text-copperlight transition-colors">{tour.title}</p>
                      <p className="font-mono text-[11px] text-sand mt-1">{tour.dates_label}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-copper ml-auto shrink-0" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactCTA to="/contatti" bg={assets && assets.cta_bg && assets.cta_bg.leader} testId="leader-cta" compact />

      <Footer />
    </div>
  );
}
